import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { cn } from '@ideoz/ui';
import { hotEditStore, nextSmartPrompt } from '../../stores/hotEdit';
import { GLASS, RISE } from './surface';

const WIDTH = 272;
const GAP = 10;
/** Enough for the title, two lines of body, the answers and the keyboard hint. */
const CARD_HEIGHT = 168;
/** The question waits this long after the change, so the founder sees the change first. */
const ENTRY_DELAY_MS = 100;

/**
 * The editor's one question, under the element it is about (Prototype Editor UX plan, step 4;
 * Editor Glass Pass plan).
 *
 * It appears after the change is already showing, never before, with a pointer up to the ring
 * so it visibly belongs to the element. Enter takes the broad answer, Escape the narrow one, and
 * so does a click anywhere else - the narrow answer is the one that cannot surprise anyone. The
 * keys are printed on the card. One question at a time: the next waits in the store's queue.
 */
export function SmartPrompt({
  frame,
}: {
  frame: { top: number; left: number; width: number; height: number } | null;
}): React.ReactElement | null {
  const prompt = useStore(hotEditStore.smartPrompt);
  const rect = useStore(hotEditStore.selectionRect);
  const [shown, setShown] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // A moment after the change lands, the question rises.
  useEffect(() => {
    setShown(false);
    if (!prompt) return undefined;
    const timer = window.setTimeout(() => setShown(true), ENTRY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [prompt]);

  useEffect(() => {
    if (!prompt || !shown) return undefined;
    const answer = (run: () => void) => {
      nextSmartPrompt();
      run();
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        ev.stopPropagation();
        answer(prompt.broad.run);
      } else if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        answer(prompt.narrow.run);
      }
    };
    const onPointerDown = (ev: PointerEvent) => {
      if (cardRef.current?.contains(ev.target as Node)) return;
      answer(prompt.narrow.run);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [prompt, shown]);

  if (!prompt || !rect || !frame || !shown) return null;

  const answer = (run: () => void) => {
    nextSmartPrompt();
    run();
  };
  // Under the element, unless that would put the card off the bottom or the right of the
  // preview - then it goes above it, and slides left to stay in view.
  const below = frame.top + rect.top + rect.height + GAP;
  const fitsBelow = below + CARD_HEIGHT <= frame.top + frame.height;
  const top = fitsBelow ? below : Math.max(frame.top + 8, frame.top + rect.top - CARD_HEIGHT - GAP);
  const left = Math.max(
    frame.left + 8,
    Math.min(frame.left + rect.left, frame.left + frame.width - WIDTH - 8),
  );

  return (
    <div
      ref={cardRef}
      className={cn(GLASS, RISE, 'absolute z-40 rounded-[14px] px-3 pb-2.5 pt-3')}
      style={{ top, left, width: WIDTH }}
      role="dialog"
      aria-label={prompt.title}
      data-testid="hot-edit-smart-prompt"
      onMouseDown={(e) => e.preventDefault()}
    >
      <span
        aria-hidden
        className="absolute -top-1.5 left-[22px] h-3 w-3 rotate-45"
        style={{ background: 'var(--editor-glass)', boxShadow: 'inset 1px 1px 0 0 var(--editor-edge)' }}
      />
      <p className="text-[13.5px] font-bold leading-tight">{prompt.title}</p>
      <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--editor-ink-2)' }}>
        {prompt.body}
      </p>
      <div className="mt-2.5 flex items-center gap-1.5">
        <button
          type="button"
          className="h-[30px] flex-1 rounded-[9px] text-xs font-semibold text-white"
          style={{ background: 'var(--editor-accent)' }}
          onClick={() => answer(prompt.broad.run)}
          data-testid="hot-edit-smart-broad"
        >
          {prompt.broad.label}
        </button>
        <button
          type="button"
          className="h-[30px] flex-1 rounded-[9px] text-xs font-semibold hover:bg-black/5 dark:hover:bg-white/10"
          style={{ boxShadow: 'inset 0 0 0 1px rgba(127,127,127,0.35)' }}
          onClick={() => answer(prompt.narrow.run)}
          data-testid="hot-edit-smart-narrow"
        >
          {prompt.narrow.label}
        </button>
      </div>
      <p className="mt-2 text-[10.5px]" style={{ color: 'var(--editor-ink-2)' }}>
        <Key>↵</Key> {prompt.broad.label} · <Key>esc</Key> {prompt.narrow.label}
      </p>
    </div>
  );
}

function Key({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <kbd
      className="rounded px-1 font-mono text-[10px] font-medium"
      style={{ boxShadow: 'inset 0 0 0 1px rgba(127,127,127,0.4)' }}
    >
      {children}
    </kbd>
  );
}
